import {
  ChatStreamingStepStatus,
  ISSEvent,
  ISSEventMessageType,
  ISSMessage,
  ISSMessages,
  ISuggestion,
  SseTypeEnum,
  deepSanitize,
  isAutoNavigateConfig,
  isValidRelativeRoute,
} from '@fly/shared';
import { t } from 'i18next';
import log from 'loglevel';

export type SSEStreamOptions<TMessage, TPayload = any> = {
  url: string;
  payload: TPayload;
  onMessage?: (message: ISSEvent<TMessage>) => void;
  onError?: (error: any) => void;
  dispatch?: (action: any) => void;
  actionCreator?: (message: ISSEvent<any>) => any;
  onLogout: () => void;
};

export type SSEStreamHandle = {
  close: () => void;
  id: string;
};

const getMessageType = (line: string = ''): ISSEventMessageType => {
  let key = ISSEventMessageType.DATA.toString();
  if (line?.startsWith('event:')) {
    key = line.split('event:')[1].trim();
  }
  return (
    ISSEventMessageType[
      key.toUpperCase() as keyof typeof ISSEventMessageType
    ] || 'unknown'
  );
};

const activeConnections = new Map<string, AbortController>();

const isAbortedStreamError = (err: unknown): boolean => {
  const errorName =
    typeof err === 'object' && err !== null && 'name' in err
      ? String((err as { name?: unknown }).name || '')
      : '';
  const errorMessage =
    typeof err === 'object' && err !== null && 'message' in err
      ? String((err as { message?: unknown }).message || '')
      : '';

  if (errorName === 'AbortError') {
    return true;
  }

  const normalizedMessage = errorMessage.toLowerCase();
  return (
    normalizedMessage.includes('bodystreambuffer was aborted') ||
    normalizedMessage.includes('body stream buffer was aborted')
  );
};

/**
 * Creates a Server-Sent Events (SSE) stream for receiving messages from a server.
 * @param options - Configuration options for the SSE stream.
 * @returns An object with a close method to stop the stream and an id for the stream.
 */
export function createSSEPostStream<TMessage = any, TPayload = any>(
  options: SSEStreamOptions<TMessage, TPayload>
): SSEStreamHandle {
  const {
    url,
    payload,
    onMessage,
    onError,
    dispatch,
    actionCreator,
    onLogout,
  } = options;
  closeAllSSEStreams();
  const controller = new AbortController();
  const streamId = url;
  activeConnections.set(url, controller);

  fetch(url, {
    method: 'POST',
    body: JSON.stringify(deepSanitize(payload)),
    headers: {
      Accept: 'text/event-stream',
      'Content-Type': 'application/json',
      'X-Fly-Client-Type': 'web',
    },
    signal: controller.signal,
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(
          `HTTP error! status: ${response.status} ${response.statusText}`
        );
      }
      const reader = response.body?.getReader();
      if (!reader) throw new Error('No body in response');
      const decoder = new TextDecoder();
      let buffer = '';

      function read() {
        reader
          ?.read()
          .then(({ done, value }) => {
            if (done) {
              activeConnections.delete(streamId);
              log.log(`SSE stream ${streamId} closed.`);
              return;
            }

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || ''; // keep partial

            for (const line of lines) {
              const eventType = getMessageType(line);
              let parsed = null;
              const dataStrType = `${ISSEventMessageType.DATA.toString()}`;

              if (line?.startsWith(`${dataStrType}:`)) {
                const json = line.replace(/^data:\s*/, '');

                try {
                  parsed = JSON.parse(json);
                } catch (err) {
                  log.error('SSE parse error:', err);
                }
              }
              const sseMessage: ISSEvent<TMessage> = {
                type: eventType,
                data: parsed,
              };

              if (eventType.length) {
                onMessage?.(sseMessage);
                if (dispatch && actionCreator) {
                  dispatch(actionCreator(sseMessage));
                }
              }
            }
            read();
          })
          .catch((err) => {
            activeConnections.delete(streamId);
            if (isAbortedStreamError(err)) {
              log.log(`SSE stream ${streamId} aborted during read.`);
              return;
            }
            const errorMessage =
              err instanceof Error
                ? err.message
                : 'Stream reading error occurred';
            log.error('SSE read error:', errorMessage);
            onError?.(new Error(errorMessage));
          });
      }

      read();
    })
    .catch((err) => {
      if (isAbortedStreamError(err)) {
        activeConnections.delete(streamId);
        log.log(`SSE stream ${streamId} aborted before completion.`);
        return;
      }
      const errorEvent: ISSEvent<Record<string, ISSMessages>> = {
        type: ISSEventMessageType.ERROR,
        data: err.message,
      };
      dispatch?.(actionCreator?.(errorEvent));
      activeConnections.delete(streamId);
      const errorMessage =
        err instanceof Error ? err.message : 'Network error occurred';
      log.error('SSE fetch error:', errorMessage);
      onError?.(new Error(errorMessage));
      if (err.message.includes('401')) {
        onLogout?.();
      }
    });

  return {
    id: streamId,
    close: () => {
      const ctrl = activeConnections.get(streamId);
      if (ctrl) {
        ctrl.abort();
        activeConnections.delete(streamId);
        log.log(`Stream ${streamId} aborted`);
      }
    },
  };
}

export function closeAllSSEStreams() {
  for (const [id, controller] of activeConnections.entries()) {
    controller.abort();
    log.log(`Aborted stream ${id}`);
  }
  activeConnections.clear();
}

export type StreamingStepInput = {
  label: string;
  status: ChatStreamingStepStatus;
  href?: string;
};

export interface SSMessageHandlers {
  onError: (errorMessage: string) => void;
  onStreamEnd: () => void;
  onTitleUpdate: (title: string) => void;
  onNavigation: (route: string) => void;
  onSuggestions: (suggestions: ISuggestion[]) => void;
  onContentChunk: (content: string) => void;
  onCacheInvalidation: (tags: string[]) => void;
  onStreamingStep: (step: StreamingStepInput) => void;
  onFinalContent: (content: string) => void;
}

const REPLY_TO_USER_TOOL = 'reply_to_user';
const SEARCH_DOCS_TOOL = 'search_docs';

const humanizeToolName = (name: string): string =>
  name
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

export const formatToolCallStepLabel = (
  toolName: string,
  args?: Record<string, unknown>
): string => {
  if (toolName === SEARCH_DOCS_TOOL && typeof args?.query === 'string') {
    return t('app.dashboard.chat.content.searching_docs', {
      defaultValue: 'Searching docs: {{query}}',
      query: args.query,
    });
  }
  return humanizeToolName(toolName);
};

export const formatArtifactStepLabel = (displayName: string): string =>
  t('app.dashboard.chat.content.reading_doc', {
    defaultValue: 'Reading {{name}}',
    name: displayName,
  });

const emitStreamingStep = (
  handlers: Partial<SSMessageHandlers>,
  label: string,
  status: ChatStreamingStepStatus = 'active',
  href?: string
) => {
  const trimmed = label.trim();
  if (!trimmed) return;
  handlers.onStreamingStep?.({ label: trimmed, status, href });
};

const handleWorkerMessage = (
  workerMessage: ISSMessage,
  handlers: Partial<SSMessageHandlers>
) => {
  for (const tc of workerMessage.tool_calls ?? []) {
    const toolName = tc.name ?? '';
    if (!toolName) continue;

    if (toolName === REPLY_TO_USER_TOOL) {
      const finalContent = tc.args?.content;
      if (typeof finalContent === 'string' && finalContent.length > 0) {
        handlers.onFinalContent?.(finalContent);
      }
      continue;
    }

    emitStreamingStep(
      handlers,
      formatToolCallStepLabel(
        toolName,
        tc.args as Record<string, unknown> | undefined
      )
    );
  }

  if (workerMessage.content) {
    handlers.onContentChunk?.(workerMessage.content);
  }
};

const handleExecuteActions = (
  executeData: ISSMessages,
  handlers: Partial<SSMessageHandlers>
) => {
  for (const msg of executeData.messages ?? []) {
    for (const artifact of msg.artifact ?? []) {
      if (artifact.display_name) {
        emitStreamingStep(
          handlers,
          formatArtifactStepLabel(artifact.display_name),
          'done',
          artifact.url
        );
      }
    }
  }
};

const handlePartialResult = (
  pr: Record<string, unknown>,
  handlers: Partial<SSMessageHandlers>
) => {
  const step = pr.step as string | undefined;
  const type = pr.type as string | undefined;
  const content = (pr.content as string) || '';

  if (step === 'start' && content) {
    emitStreamingStep(handlers, content);
    return;
  }

  if (step === 'end' && type === 'tool_call' && content) {
    emitStreamingStep(handlers, content, 'done');

    const isMutation = MUTATION_SUFFIXES.some((s) =>
      content.toLowerCase().endsWith(s)
    );
    if (!isMutation) return;

    const entity = content.split(' ')[0].toLowerCase();
    const tags = ENTITY_CACHE_TAGS[entity];
    if (tags) handlers.onCacheInvalidation?.(tags);
  }
};

export const extractSuggestionFromToolCalls = (
  message: ISSMessages
): string | undefined => {
  for (const msg of message?.messages ?? []) {
    for (const tc of msg?.tool_calls ?? []) {
      const suggestions = tc?.args?.suggestions;
      if (Array.isArray(suggestions) && suggestions.length > 0) {
        const first = suggestions[0];
        return typeof first === 'string' ? first : first?.content;
      }
    }
  }
  return undefined;
};

export function handleSSMessage(
  ssMessage: ISSEvent<Record<string, ISSMessages>>,
  handlers: Partial<SSMessageHandlers>
): void {
  const messageData = ssMessage?.data || {};

  if (ssMessage.type === ISSEventMessageType.ERROR.toString()) {
    handlers.onError?.(
      t('app.common.errors.global_error', { defaultValue: 'An error occurred' })
    );
    return;
  }

  if (ssMessage.type === ISSEventMessageType.END.toString()) {
    handlers.onStreamEnd?.();
    return;
  }

  const messageType = Object.keys(ssMessage.data || {})[0];
  switch (messageType) {
    case SseTypeEnum.TOPIC_SUGGESTER.toString():
    case SseTypeEnum.TOPIC_SUGGESTER_ORCHESTRATOR.toString(): {
      const message: ISSMessages = messageData[messageType];
      const topic = message.topic_suggestions?.[0] || [];
      let title: string | undefined =
        topic?.suggestions?.[0]?.content || undefined;
      if (!title) {
        title = extractSuggestionFromToolCalls(message);
      }
      if (title) handlers.onTitleUpdate?.(title);
      break;
    }

    case SseTypeEnum.NAVIGATION_SUGGESTER.toString(): {
      if (!ssMessage.data) break;
      const message: ISSMessages =
        messageData[SseTypeEnum.NAVIGATION_SUGGESTER.toString()];
      const navigations =
        message.navigation_suggestions?.[0]?.suggestions || [];
      const route = navigations?.[0]?.content;
      if (
        !route ||
        !isValidRelativeRoute(route) ||
        !isAutoNavigateConfig() ||
        BLOCKED_AUTO_NAVIGATION_PREFIXES.some((prefix) =>
          route.startsWith(prefix)
        )
      )
        break;
      if (route === window.location.pathname) break;
      handlers.onNavigation?.(route);
      break;
    }

    case SseTypeEnum.FOLLOWUP_SUGGESTER.toString(): {
      if (!ssMessage.data) break;
      const message: ISSMessages =
        messageData[SseTypeEnum.FOLLOWUP_SUGGESTER.toString()];
      const suggestions: ISuggestion[] =
        message.followup_suggestions?.[0]?.suggestions || [];
      handlers.onSuggestions?.(suggestions);
      break;
    }

    case SseTypeEnum.WORKER.toString():
    case SseTypeEnum.WORKER_ORCHESTRATOR.toString(): {
      if (!ssMessage.data) break;
      const workerData = messageData[messageType];
      if (!workerData?.messages?.length) break;
      handleWorkerMessage(workerData.messages[0], handlers);
      break;
    }

    case SseTypeEnum.EXECUTE_ACTIONS.toString(): {
      if (!ssMessage.data) break;
      const executeData = messageData[SseTypeEnum.EXECUTE_ACTIONS.toString()];
      if (!executeData) break;
      handleExecuteActions(executeData, handlers);
      break;
    }

    case 'partial_result': {
      const pr = (messageData as Record<string, unknown>)['partial_result'] as
        | Record<string, unknown>
        | undefined;
      if (!pr) break;
      handlePartialResult(pr, handlers);
      break;
    }
  }
}

const MUTATION_SUFFIXES = ['created', 'updated', 'deleted'];
const BLOCKED_AUTO_NAVIGATION_PREFIXES = ['/help', '/dashboard/help'];

const ENTITY_CACHE_TAGS: Record<string, string[]> = {
  environment: ['environments'],
};
